import React, { useState } from 'react';
import { StarIcon, UserIcon, ThumbsUpIcon } from 'lucide-react';
import { Comment, formatDate, currentUser } from '../utils/mockData';
interface CommentsSectionProps {
  comments: Comment[];
  onAddComment: (rating: number, comment: string) => void;
}
const CommentsSection: React.FC<CommentsSectionProps> = ({
  comments,
  onAddComment
}) => {
  const [rating, setRating] = useState(0);
  const [hoveredRating, setHoveredRating] = useState(0);
  const [commentText, setCommentText] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (rating === 0 || !commentText.trim()) return;
    setIsSubmitting(true);
    setTimeout(() => {
      onAddComment(rating, commentText);
      setRating(0);
      setCommentText('');
      setIsSubmitting(false);
    }, 500);
  };
  return <div className="bg-white rounded-lg shadow-md p-6" data-id="element-31">
      <h2 className="text-xl font-semibold text-gray-900 mb-6" data-id="element-32">
        Reviews & Ratings
      </h2>
      {/* Add Comment Form */}
      <div className="mb-8 p-4 bg-gray-50 rounded-lg" data-id="element-33">
        <h3 className="text-lg font-medium text-gray-900 mb-4" data-id="element-34">
          Leave a Review
        </h3>
        <form onSubmit={handleSubmit} data-id="element-35">
          <div className="mb-4" data-id="element-36">
            <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-37">
              Your Rating*
            </label>
            <div className="flex items-center space-x-1" data-id="element-38">
              {[1, 2, 3, 4, 5].map(star => <button key={star} type="button" onClick={() => setRating(star)} onMouseEnter={() => setHoveredRating(star)} onMouseLeave={() => setHoveredRating(0)} className="focus:outline-none" data-id="element-39">
                  <StarIcon className={`h-8 w-8 transition-colors ${star <= (hoveredRating || rating) ? 'text-yellow-500 fill-yellow-500' : 'text-gray-300'}`} data-id="element-40" />
                </button>)}
              {rating > 0 && <span className="ml-2 text-sm text-gray-600" data-id="element-41">
                  {rating} {rating === 1 ? 'star' : 'stars'}
                </span>}
            </div>
          </div>
          <div className="mb-4" data-id="element-42">
            <label htmlFor="comment" className="block text-sm font-medium text-gray-700 mb-2" data-id="element-43">
              Your Review*
            </label>
            <textarea id="comment" value={commentText} onChange={e => setCommentText(e.target.value)} rows={4} className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500" placeholder="Share your thoughts about this property..." required data-id="element-44" />
          </div>
          <button type="submit" disabled={isSubmitting || rating === 0 || !commentText.trim()} className={`bg-blue-600 text-white font-medium px-6 py-2 rounded-md transition duration-200 ${isSubmitting || rating === 0 || !commentText.trim() ? 'opacity-50 cursor-not-allowed' : 'hover:bg-blue-700'}`} data-id="element-45">
            {isSubmitting ? 'Submitting...' : 'Submit Review'}
          </button>
        </form>
      </div>
      {/* Comments List */}
      <div className="space-y-6" data-id="element-46">
        {comments.length === 0 ? <div className="text-center py-8" data-id="element-47">
            <p className="text-gray-500" data-id="element-48">
              No reviews yet. Be the first to review this property!
            </p>
          </div> : comments.map(comment => <div key={comment.id} className="border-b border-gray-200 pb-6 last:border-b-0" data-id="element-49">
              <div className="flex items-start" data-id="element-50">
                <div className="flex-shrink-0" data-id="element-51">
                  {comment.userAvatar ? <img src={comment.userAvatar} alt={comment.userName} className="h-10 w-10 rounded-full object-cover" data-id="element-52" /> : <div className="h-10 w-10 rounded-full bg-gray-200 flex items-center justify-center" data-id="element-53">
                      <UserIcon className="h-6 w-6 text-gray-500" data-id="element-54" />
                    </div>}
                </div>
                <div className="ml-4 flex-1" data-id="element-55">
                  <div className="flex items-center justify-between" data-id="element-56">
                    <div data-id="element-57">
                      <h4 className="text-sm font-medium text-gray-900" data-id="element-58">
                        {comment.userName}
                      </h4>
                      <p className="text-xs text-gray-500" data-id="element-59">
                        {formatDate(comment.createdAt)}
                      </p>
                    </div>
                    <div className="flex items-center" data-id="element-60">
                      {[...Array(5)].map((_, index) => <StarIcon key={index} className={`h-4 w-4 ${index < comment.rating ? 'text-yellow-500 fill-yellow-500' : 'text-gray-300'}`} data-id="element-61" />)}
                    </div>
                  </div>
                  <p className="mt-2 text-sm text-gray-700" data-id="element-62">
                    {comment.comment}
                  </p>
                </div>
              </div>
            </div>)}
      </div>
    </div>;
};
export default CommentsSection;